import {
	DateString,
	DialogBackdropState,
	FormattedText,
	InlineOptionSelectorItem,
	OptionsMenu,
	Pagination,
	SearchInputForm,
	SelectItem,
	TableView,
	TableViewNoResultConfig,
	TagList,
	ToolbarAction,
	ToolbarRegularAction,
	Typography,
	useConfirmationDialog
} from "client/Components";
import { contains, normalizeForSearch } from "client/Data/searching";
import {
	DataSourceSortableState,
	DataSourceSortDirection
} from "client/Data/sorting";
import { FilterForm } from "client/Features/Settings/FilterForm/FilterForm";
import {
	AlertManager,
	createLocalDataSource,
	useAlerts,
	useDeepComparison,
	useFilterHandler,
	useForceRerender,
	useSelection,
	useSortHandler,
	useUnmountedCancellation,
	useUserTimezone
} from "client/Hooks";
import {
	generateTrackingId,
	isNil,
	isNotNil,
	isNotNilOrEmpty
} from "client/Misc/utils";
import { hasImplicitDependencyOn } from "client/Misc/reactUtils";
import {
	ReactElement,
	useCallback,
	useEffect,
	useMemo,
	useState
} from "react";
import { localize } from "client_legacy/ndaw/culture";
import {
	archiveEvents,
	deleteEvents,
	EventListItemModel,
	EventListModel,
	EventStatus,
	listEvents,
	LocationType
} from "client/Api/EventsManagement/eventsManagement";
import { TimeZone } from "client/DateAndTime/timezones";
import { HubManagementDialogContentLayout } from "../../HubManagementDialogContentLayout";
import { useEditEventDialog } from "../../../EventsManagement/EditEventDialog/EditEventDialog";
import { isCancelledError } from "client_legacy/ndaw/cancellation";
import {
	ActionKind,
	buildActionsForTopBar,
	deselectAllTitle
} from "client_legacy/ndaw/actions";
import { useCreateHubTemplateDialog } from "../../../Templates/CreateHubTemplateDialog";
import { useCreateHubItemDialog } from "client/Features/Hub/CreateHubItemDialog/CreateHubItemDialog";
import { HubItemKind } from "client/Api/Hub/hubItemCategories";

export type EventsOverviewProps = {
	onParentBackdropChange: (backdrop: DialogBackdropState) => void;
	isMobile: boolean;
	isResponsive: boolean;
	onItemsChanged: () => void;
};

export const EventsOverview = (props: EventsOverviewProps): ReactElement => {
	const {
		onParentBackdropChange,
		isMobile,
		isResponsive,
		onItemsChanged
	} = props;

	const [eventsList, setEventsList] = useState<EventListModel>({
		events: [],
		categories: [],
		canCreateTemplates: false
	});

	const [selectedEvents, setSelectedEvents] = useState<readonly EventListItemModel[]>([]);
	const [isBusy, setIsBusy] = useState<boolean>(false);
	const [searchTerm, setSearchTerm] = useState<string>("");

	const [filter, setFilter] = useState<FilterState>({
		title: "",
		activeCategoryId: null,
		createdBy: "",
		modifiedBy: "",
		status: null,
		locationType: null
	});

	const [currentPageNumber, setCurrentPageNumber] = useState<number>(0);

	const [sortingState, setSortingState] = useState<DataSourceSortableState<EventListItemModel>>({
		direction: DataSourceSortDirection.Descending,
		propertyName: "publicationDate"
	});

	const alertManager: AlertManager = useAlerts();

	const globalCancellation: AbortSignal = useUnmountedCancellation();
	const [reload, dataVersion] = useForceRerender();

	const [editEventDialog, openEditEventDialog] = useEditEventDialog();
	const [createHubItemDialog, openCreateHubItemDialog] = useCreateHubItemDialog();
	const [confirmationDialog, openConfirmationDialog] = useConfirmationDialog();
	const [createHubTemplateDialog, openCreateHubTemplateDialog] = useCreateHubTemplateDialog();

	const editOrCreateEventHandler = useCallback(
		async (event?: EventListItemModel): Promise<void> => {
			onParentBackdropChange("invisible");
			let result: TryResult<void>;

			if (isNil(event?.id)) {
				result = await openCreateHubItemDialog({
					kind: HubItemKind.Event
				});
			} else {
				result = await openEditEventDialog({
					mode: {
						kind: "edit",
						eventId: event.id
					}
				});
			}

			onParentBackdropChange("visible");

			if (result.wasSuccessful) {
				reload();
				onItemsChanged();
			}
		},
		[
			onParentBackdropChange,
			openEditEventDialog,
			reload,
			openCreateHubItemDialog,
			onItemsChanged
		]
	);

	const deleteEventsHandler = useCallback(
		async (events: readonly EventListItemModel[]): Promise<void> => {
			try {
				const result: TryResult<void> = await openConfirmationDialog({
					title: localize("_AreYouSureYouWantToDeleteThisItem(s)"),
					confirmText: localize("_Delete"),
					trackingId:"event-delete-confirmation-dialog",
				});

				if (!result.wasSuccessful) {
					return;
				}

				setIsBusy(true);
				await deleteEvents(
					events.map(a => a.id),
					globalCancellation
				);
				setSelectedEvents([]);
				setIsBusy(false);
				reload();
				onItemsChanged();
			} catch(e) {
				if (isCancelledError(e)) {
					return;
				}
				alertManager.showError(localize("_GeneralErrorMessage"));
				setIsBusy(false);
			}
		},
		[
			alertManager,
			globalCancellation,
			openConfirmationDialog,
			reload,
			onItemsChanged
		]
	);

	const archiveEventsHandler = useCallback(
		async (events: readonly EventListItemModel[]): Promise<void> => {
			try {
				const result: TryResult<void> = await openConfirmationDialog({
					title: localize("_AreYouSureYouWantToArchiveThisItem(s)"),
					confirmText: localize("_Archive"),
					trackingId:"event-archive-confirmation-dialog",
				});

				if (!result.wasSuccessful) {
					return;
				}

				setIsBusy(true);
				await archiveEvents(
					events.map(a => a.id),
					globalCancellation
				);
				setIsBusy(false);
				reload();
				onItemsChanged();
			} catch(e) {
				if (isCancelledError(e)) {
					return;
				}
				alertManager.showError(localize("_GeneralErrorMessage"));
				setIsBusy(false);
			}
		},
		[
			alertManager,
			openConfirmationDialog,
			reload,
			onItemsChanged,
			globalCancellation
		]
	);

	const saveAsTemplateHandler = useCallback(
		async (item: EventListItemModel): Promise<void> => {
			onParentBackdropChange("invisible");

			await openCreateHubTemplateDialog({
				kind: HubItemKind.Event,
				basedOn: {
					hubItemId: item.id
				}
			});

			onParentBackdropChange("visible");
		},
		[
			onParentBackdropChange,
			openCreateHubTemplateDialog
		]
	);

	const duplicateHandler = useCallback(
		async (item: EventListItemModel): Promise<void> => {
			onParentBackdropChange("invisible");

			const {
				wasSuccessful
			} = await openEditEventDialog({
				mode: {
					kind: "duplicate",
					eventToCopyId: item.id
				}
			});

			if (wasSuccessful) {
				reload();
				onItemsChanged();
			}

			onParentBackdropChange("visible");
		},
		[
			onItemsChanged,
			onParentBackdropChange,
			openEditEventDialog,
			reload
		]
	);

	const {
		sortingFor
	} = useSortHandler(sortingState, setSortingState);

	const {
		filteringFor
	} = useFilterHandler<FilterState>(filter, setFilter);

	const itemsModifiersVersion: number = useDeepComparison([
		sortingState,
		searchTerm,
		filter
	]);

	const userTimezone: TimeZone = useUserTimezone();

	useEffect(
		() => setCurrentPageNumber(0),
		[itemsModifiersVersion]
	);

	const categoryIdOptions = useMemo(
		(): readonly InlineOptionSelectorItem<number | null>[] => {
			return [
				{
					text: localize("_AllEvents"),
					value: null,
					valueTrackingId: "all-events"
				},
				...eventsList.categories.map(c => ({
					text: c.name,
					value: c.id
				}))
			];
		},
		[eventsList.categories]
	);

	useEffect(
		() => {
			hasImplicitDependencyOn(dataVersion);

			async function fetchData(signal: AbortSignal): Promise<void> {
				try {
					setIsBusy(true);

					const eventsList: EventListModel = await listEvents(signal);
					setEventsList(eventsList);

					setIsBusy(false);
				} catch (e) {
					if (isCancelledError(e)) {
						return;
					}

					alertManager.showError(localize("_GeneralErrorMessage"));
					setIsBusy(false);
				}
			}

			const cancellation = new AbortController();
			fetchData(cancellation.signal);

			return () => cancellation.abort();
		},
		[alertManager, dataVersion]
	);

	const {
		currentItems,
		pagination
	} = useEventsDataSource(
		eventsList.events,
		{
			searchTerm,
			filtering: filter,
			pagination: {
				currentPageNumber
			},
			sorting: sortingState
		}
	);

	const {
		selectionFor,
		multiSelection
	} = useSelection(
		currentItems,
		"id",
		{
			isMultiSelect: true,
			selectedItems: selectedEvents,
			setSelectedItems: setSelectedEvents
		}
	);

	const actionContext = useMemo(
		(): EventActionContext => {
			return {
				selectedEvents,
				canCreateTemplates: eventsList.canCreateTemplates,
				isMobile
			};
		},
		[
			eventsList.canCreateTemplates,
			isMobile,
			selectedEvents
		]
	);

	const selectionActions = useMemo(
		(): readonly EventAction[] => {
			return getSelectionActions(
				() => setSelectedEvents([])
			);
		},
		[]
	);

	const mainAction = useMemo(
		(): ToolbarRegularAction<EventActionContext> => {
			return getMainAction(
				editOrCreateEventHandler
			);
		},
		[editOrCreateEventHandler]
	);

	const contextualActions = useMemo(
		(): readonly EventAction[] => {
			return getContextualActions(
				editOrCreateEventHandler,
				deleteEventsHandler,
				archiveEventsHandler,
				saveAsTemplateHandler,
				duplicateHandler
			);
		},
		[
			archiveEventsHandler,
			deleteEventsHandler,
			duplicateHandler,
			editOrCreateEventHandler,
			saveAsTemplateHandler
		]
	);

	const hasSelection: boolean = selectedEvents.length > 0;

	const actions = useMemo(
		(): readonly EventAction[] => {
			return buildActionsForTopBar<EventActionContext, EventAction>(
				{
					mainActions: [mainAction],
					selectionActions,
					contextualActions
				},
				hasSelection,
				isResponsive,
				false
			);
		},
		[
			mainAction,
			selectionActions,
			contextualActions,
			hasSelection,
			isResponsive
		]
	);

	const noResultConfig = useMemo(
		(): TableViewNoResultConfig => {
			const message: string = eventsList.events.length === 0
				? localize("_ThereAreNotEventsYet")
				: isNotNilOrEmpty(searchTerm)
					? localize("_NoEventsMatchedYourSearchKeywords")
					: localize("_NoEventsInThisCategory");

			if (isNotNilOrEmpty(searchTerm)) {
				return {
					icon: "magnifying-glass",
					title: localize("_NoSearchResults"),
					message
				};
			}

			return {
				icon: "calendar-star",
				title: localize("_NoEventsYet"),
				message
			};
		},
		[
			searchTerm,
			eventsList.events
				.length
		]
	);

	return (
		<HubManagementDialogContentLayout
			title={localize("_Events")}
			isMobile={isMobile}
			isResponsive={isResponsive}
			actions={actions}
			actionContext={actionContext}
			hasSelection={hasSelection}
			trackingId="events-overview"
			isBusy={isBusy}
		>
			<HubManagementDialogContentLayout.Filter>
				<SearchInputForm
					onChange={setSearchTerm}
					trackingId="event-search-form"
					placeholder={
						<FormattedText
							text={localize("_FilterByProperties")}
							thingsToReplace={[
								{
									what: "{{Properties}}",
									filler: (
										<Typography
											weight="bold"
											isItalic
										>
											{localize("_Title")}
										</Typography>
									)
								}
							]}
						/>
					}
					height="large"
					icon="filter"
				/>
				<OptionsMenu
					value={filteringFor("activeCategoryId").value}
					onChange={filteringFor("activeCategoryId").setValue}
					options={categoryIdOptions}
					trackingId="event-category-selector"
					title={localize("_Category")}
					icon="filter"
					buttonSize="large"
				/>
			</HubManagementDialogContentLayout.Filter>
			<HubManagementDialogContentLayout.Table>
				<TableView
					trackingId="event-grid"
					multiSelectable={multiSelection}
					items={currentItems}
					height="stretch"
					itemKey="id"
					columns={[
					{
						title: localize("_Title"),
						width: 270,
						sortable: sortingFor("title"),
						filterable: filteringFor("title"),
						trackingId: "title"
					},
					{
						title: localize("_PublicationDate"),
						width: 220,
						sortable: sortingFor("publicationDate"),
						trackingId: "publication-date"
					},
					{
						title: localize("_StartEvent"),
						width: 220,
						sortable: sortingFor("startDate"),
						trackingId: "start-event-date"
					},
					{
						title: localize("_EndEvent"),
						width: 220,
						sortable: sortingFor("endDate"),
						trackingId: "end-event-date"
					},
					{
						title: localize("_DateModified"),
						width: 220,
						sortable: sortingFor("modifiedDate"),
						trackingId: "modified-date"
					},
					{
						title: localize("_Status"),
						width: 150,
						sortable: sortingFor("status"),
						filterable: {
							columnFilterableFormFactory: (onClose) => (
								<FilterForm
									filterable={filteringFor("status")}
									onClose={onClose}
									statusFilterOptions={statusFilterOptions}
								/>
							),
							isActive: isNotNil(filter.status)
						},
						trackingId: "status"
					},
					{
						title: localize("_CreatedBy"),
						width: 160,
						sortable: sortingFor("createdByUserName"),
						filterable: filteringFor("createdBy"),
						trackingId: "created-by"
					},
					{
						title: localize("_Modified"),
						width: 160,
						sortable: sortingFor("modifiedByUserName"),
						filterable: filteringFor("modifiedBy"),
						trackingId: "modified-by"
					},
					{
						title: localize("_Attendees"),
						width: 160,
						sortable: sortingFor("totalAttendees"),
						trackingId: "total-attendees"
					},
					{
						title: localize("_EventType"),
						width: 150,
						sortable: sortingFor("locationType"),
						filterable: {
							columnFilterableFormFactory: (onClose) => (
								<FilterForm
									filterable={filteringFor("locationType")}
									onClose={onClose}
									statusFilterOptions={locationTypeFilterOptions}
								/>
							),
							isActive: isNotNil(filter.locationType)
						},
						trackingId: "location-type"
					},
					{
						title: localize("_Comments"),
						width: 90,
						sortable: sortingFor("commentsCount"),
						trackingId: "comments-count"
					},
					{
						title: localize("_Reactions"),
						width: 90,
						sortable: sortingFor("reactionsCount"),
						trackingId: "likes-count"
					},
					{
						title: localize("_Category"),
						width: 90,
						sortable: sortingFor("categoryName"),
						trackingId: "category-name"
					},
					{
						title: localize("_Tags"),
						width: 200,
						trackingId: "tags",
						isExpensiveToRender: true
					}
					]}
					noResultConfig={noResultConfig}
				>
					{k => (
						<TableView.Row
							key={k.id}
							selectable={selectionFor(k)}
							onExecute={() => editOrCreateEventHandler(k)}
							trackingId={k.id.toString()}
						>
							<TableView.Cell>
								{k.title}
							</TableView.Cell>
							<TableView.Cell>
								{isNotNil(k.publicationDate) && (
									<DateString
										value={k.publicationDate}
										format="RelativeDayWithTime"
										timezone={userTimezone}
									/>
								)}
							</TableView.Cell>
							<TableView.Cell>
								{isNotNil(k.startDate) && (
									<DateString
										value={k.startDate}
										format="RelativeDayWithTime"
										timezone={userTimezone}
									/>
								)}
							</TableView.Cell>
							<TableView.Cell>
								{isNotNil(k.endDate) && (
									<DateString
										value={k.endDate}
										format="RelativeDayWithTime"
										timezone={userTimezone}
									/>
								)}
							</TableView.Cell>
							<TableView.Cell>
								<DateString
									value={k.modifiedDate}
									format="RelativeDayWithTime"
									timezone={userTimezone}
								/>
							</TableView.Cell>
							<TableView.Cell>
								{localize(EventStatusToLocalizationKeyMap[k.status] )}
							</TableView.Cell>
							<TableView.Cell>
								{k.createdByUserName}
							</TableView.Cell>
							<TableView.Cell>
								{k.modifiedByUserName}
							</TableView.Cell>
							<TableView.Cell>
								{k.totalAttendees}
							</TableView.Cell>
							<TableView.Cell>
								{localize(EventTypeToLocalizationKeyMap[k.locationType] )}
							</TableView.Cell>
							<TableView.Cell>
								{k.commentsCount}
							</TableView.Cell>
							<TableView.Cell>
								{k.reactionsCount}
							</TableView.Cell>
							<TableView.Cell>
								{k.categoryName}
							</TableView.Cell>
							<TableView.Cell>
								<TagList
									trackingId={generateTrackingId("event-tag-list", `|${k.id}`)}
									showMoreTagsColor="secondary"
									tags={
										k.tags
											.map(t => ({
												value: t.id,
												text: t.name,
												color: "secondary",
												valueTrackingId: t.name
											}))
									}
								/>
							</TableView.Cell>
						</TableView.Row>
					)}
				</TableView>
				<Pagination
					{...pagination}
					onPageNumberChanged={setCurrentPageNumber}
					trackingId="events-pagination"
				/>
			</HubManagementDialogContentLayout.Table>
			{isResponsive && mainAction.canExecute(actionContext) && (
				<HubManagementDialogContentLayout.MainButton
					onClick={() => mainAction.execute(actionContext)}
					trackingId={mainAction.name}
				/>
			)}
			{confirmationDialog}
			{editEventDialog}
			{createHubTemplateDialog}
			{createHubItemDialog}
		</HubManagementDialogContentLayout>
	);
};
EventsOverview.displayName="EventsList";

const statusFilterOptions: readonly SelectItem<EventStatus>[] = [
	{
		value: EventStatus.Draft,
		text: localize("_Draft")
	},
	{
		value: EventStatus.Published,
		text: localize("_Published")
	},
	{
		value: EventStatus.Planned,
		text: localize("_Planned")
	},
	{
		value: EventStatus.Archived,
		text: localize("_Archived")
	}
];

const locationTypeFilterOptions: readonly SelectItem<LocationType>[] = [
	{
		value: LocationType.Online,
		text: localize("_Online")
	},
	{
		value: LocationType.Physical,
		text: localize("_Physical")
	},
	{
		value: LocationType.Hybrid,
		text: localize("_Hybrid")
	}
];

export const EventStatusToLocalizationKeyMap: {
	[key in EventStatus]: LocalizationKey;
} = {
	[EventStatus.Published]: "_Published",
	[EventStatus.Archived]: "_Archived",
	[EventStatus.Draft]: "_Draft",
	[EventStatus.Planned]: "_Planned"
};

export const EventTypeToLocalizationKeyMap: {
	[key in LocationType]: LocalizationKey;
} = {
	[LocationType.Online]: "_Online",
	[LocationType.Physical]: "_Physical",
	[LocationType.Hybrid]: "_Hybrid"
};

type FilterState = {
	title: string;
	activeCategoryId: number | null;
	status: EventStatus | null;
	createdBy: string;
	modifiedBy: string;
	locationType: LocationType | null;
};

function applyFilter(
	items: readonly EventListItemModel[],
	filter: FilterState
): readonly EventListItemModel[] {
	const normalizedTitle: string = normalizeForSearch(filter.title);
	const normalizedCreatedByUserName: string = normalizeForSearch(filter.createdBy);
	const normalizedLastModifiedByUserName: string = normalizeForSearch(filter.modifiedBy);

	return items.filter(i =>
		(filter.activeCategoryId === null || i.categoryId === filter.activeCategoryId)
			&& (filter.status === null || i.status === filter.status)
			&& (filter.locationType === null || i.locationType === filter.locationType)
			&& (
				normalizedCreatedByUserName === ""
					|| contains(i.createdByUserName, normalizedCreatedByUserName)
			)
			&& (
				normalizedLastModifiedByUserName === ""
					|| contains(i.modifiedByUserName, normalizedLastModifiedByUserName)
			)
			&& (normalizedTitle === "" || contains(i.title, normalizedTitle))
	);
}

const useEventsDataSource = createLocalDataSource<EventListItemModel, FilterState>()
	.setConfiguration({
		search: {
			doesMatchItem: (item, searchTerm) => contains(item.title, searchTerm)
		},
		filtering: { applyFilter },
		hasPagination: true
	})
	.getHook();

type EventActionContext = {
	selectedEvents: readonly EventListItemModel[];
	canCreateTemplates: boolean;
	isMobile: boolean;
};

type EventAction = ToolbarAction<EventActionContext>;

function getSelectionActions(
	deselectHandler: () => void
): readonly EventAction[] {
	return [
		{
			kind: ActionKind.Regular,
			name: "deselectAll",
			title: (c) => deselectAllTitle(c.selectedEvents),
			icon: "circle-xmark",
			canExecute: (c) => c.selectedEvents.length > 0,
			execute: () => deselectHandler()
		},
		{
			kind: ActionKind.Divider,
			name: "deselect-all-divider",
			canExecute: (c) => !c.isMobile
				&& c.selectedEvents.length > 0
		}
	];
}

function getMainAction(
	newEventHandler: () => void
): ToolbarRegularAction<EventActionContext> {
	return {
		kind: ActionKind.Regular,
		name: "createNewEvent",
		title: localize("_New"),
		canExecute: () => true,
		execute: () => newEventHandler(),
		icon: "plus",
		isPrimary: true
	};
}

function getContextualActions(
	editEventHandler: (item: EventListItemModel) => void,
	deleteEventsHandler: (item: readonly EventListItemModel[]) => void,
	archiveEventsHandler: (item: readonly EventListItemModel[]) => void,
	saveAsTemplateHandler: (item: EventListItemModel) => void,
	duplicateEventHandler: (item: EventListItemModel) => void
): readonly EventAction[] {
	return [
		{
			kind: ActionKind.Regular,
			name: "editEvent",
			title: localize("_Edit"),
			canExecute: (c) => c.selectedEvents.length === 1,
			execute: (c) => editEventHandler(c.selectedEvents[0]),
			icon: "pen"
		},
		{
			kind: ActionKind.Regular,
			name: "deleteEvents",
			title: localize("_Delete"),
			canExecute: (c) => c.selectedEvents.length > 0,
			execute: (c) => deleteEventsHandler(c.selectedEvents),
			icon: "trash-can"
		},
		{
			kind: ActionKind.Regular,
			name: "archiveEvents",
			title: localize("_Archive"),
			canExecute: (c) => c.selectedEvents
				.some(x => x.status !== EventStatus.Archived),
			execute: (c) => archiveEventsHandler(c.selectedEvents),
			icon: "box-archive"
		},
		{
			kind: ActionKind.Regular,
			name: "duplicateEvent",
			title: localize("_Duplicate"),
			canExecute: c => c.selectedEvents.length === 1,
			execute: c => duplicateEventHandler(c.selectedEvents[0]),
			icon: "copy"
		},
		{
			kind: ActionKind.Regular,
			name: "saveAsTemplate",
			title: localize("_SaveAsTemplate"),
			canExecute: (c) => c.canCreateTemplates
				&& c.selectedEvents.length === 1,
			execute: (c) => saveAsTemplateHandler(c.selectedEvents[0]),
			icon: "floppy-disk"
		}
	];
}